import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, CircularProgress, FormControl, Grid, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { AgGridTable } from 'components/tables/AgGridTable';
import { Link, useNavigate } from 'react-router-dom';
import MDButton from 'components/MDButton';
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from '../../redux/slices/modalSlice';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { useDebounce } from 'hooks/useDebounce';
import EditIcon from 'common/customIcons/edit';
import DeleteIcon from 'common/customIcons/delete';
import { openSnackbar } from '../../redux/slices/common';
import colors from 'assets/theme/base/colors';
import { deletePromo, getPromotionList, syncOrderAdmin } from '../../redux/slices/metrc';
import SearchIcon from 'common/customIcons/search';
import { CloseRounded, Search } from '@mui/icons-material';
import DateRange from 'components/dateRangePicker/DateRange';
import { withStyles } from '@mui/styles';

const LightTooltip = withStyles((theme) => ({
    tooltip: {
        backgroundColor: theme.palette.common.white,
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: theme.shadows[1],
        fontSize: 11,
    },
}))(Tooltip);
export default function OrderMetrc() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const pageRef = useRef(0);
    const searchTermRef = useRef('');
    const mainGridApiRef = useRef('');
    const gridRefApi = useRef(null);
    const { text } = colors;
    const [searchTerm, setSearchTerm] = useState('');
    const currentTimestamp = Date.now();
    const tenHoursInMs = 10 * 60 * 60 * 1000;
    const [formData, setFormData] = useState({ startDate: currentTimestamp - tenHoursInMs, endDate: currentTimestamp })

    const searchHandler = (searchValue) => {
        setSearchTerm(searchValue);
        searchTermRef.current = searchValue;
        handleSearch(searchValue);
    };

    const handleSearch = useDebounce(() => {
        // Perform search operation with the debounced term
        pageRef.current = 0;
        searchFn();
    }, 500);

    const { syncOrder, loadingList: loading = false, promotionList = {} } = useSelector(state => state.metrc);

    const deleteHandler = (id) => {
        dispatch(setModalLoader(true));
        dispatch(deletePromo({ data: id, type: "member" })).unwrap().then((res) => {
            showError(res?.message, 'success');
            dispatch(closeCustomModalDialog());
            pageRef.current = 0;
            getRows(gridRefApi.current);
        }).catch((err) => {
            dispatch(setModalLoader(false));
            showError(err?.message, 'error');
        });
    };

    const showError = (message, errortype) => {
        if (errortype === 'success') {
            dispatch(openSnackbar({ content: message, color: 'success', icon: 'check' }));
        }
        if (errortype === 'error') {
            dispatch(openSnackbar({ content: message, color: 'error', icon: 'warning' }));
        }
    };


    const columnDefs = [

        {
            headerName: 'Order Id',
            field: 'orderId',
            suppressMenu: false,
            sortable: false,
            minWidth: 160,
            cellRenderer: (props) => {
                return props?.value ? `${props?.value}` : "-"
            },
        },

        {
            headerName: 'Receipt',
            field: 'receipt',
            suppressMenu: false,
            sortable: false,
            minWidth: 160,
            cellRenderer: (props) => {
                return props?.value ? <a
                    href={props.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#0163FF', textDecoration: 'underline' }}
                >
                    {props.value}
                </a> : "-"
            },
            headerClass: "left-aligned-cell-header",
        },


    ];

    const getRows = async (params) => {
        gridRefApi.current = params;
        const sortModel = params?.sortModel;
        let sortedColumnName = '';
        let sortDirection = '';
        if (sortModel.length > 0) {
            const sortedColumn = sortModel[0];
            sortedColumnName = sortedColumn.colId;
            sortDirection = sortedColumn.sort === 'asc' ? true : false;
        }
        dispatch(getPromotionList({ limit: 20, skip: pageRef.current, searchTerm: searchTermRef.current, startDate: new Date(formData.startDate).getTime(), endDate: new Date(formData.endDate).getTime(), userType: 'admin', [sortedColumnName]: sortDirection, type: "orders" })).unwrap().then(res => {
            const { data, total } = res;

            const modifiedBlogs = data?.map((item) => ({
                ...item,
            }));
            params.successCallback(modifiedBlogs, total);
            pageRef.current = pageRef.current + 20;
        }).catch(() => {
            params.successCallback([], 0);
        });
    };

    const searchFn = () => {
        pageRef.current = 0
        const dataSource = {
            getRows
        };
        mainGridApiRef.current.ensureIndexVisible(0, null);
        mainGridApiRef.current.setGridOption('datasource', dataSource);
    };

    const onRowClicked = (row) => {
        if (row?.colDef?.headerName !== 'Action') {
        }
    };

    const onEditClicked = (row) => {

        dispatch(openCustomModalDialog({
            modal: 'ADD_METRC_DATA',
            modalData: {
                type: "Member",
                title: 'Edit Member',
                data: row?.data,
                edit: true,
                metrcHandler: metrcHandler
            }
        }))
    };


    useEffect(() => {
        if (mainGridApiRef?.current) {
            if (loading) {
                mainGridApiRef.current.showLoadingOverlay();
            } else if (promotionList?.total === 0) {
                mainGridApiRef.current.showNoRowsOverlay();
            }
            else {
                mainGridApiRef.current.hideOverlay();
            }
        }
    }, [promotionList, loading]);

    const metrcHandler = () => {
        searchFn()

    }

    const setDateRange = (dates) => {
        if (dates && dates.length === 2) {
            setFormData({
                ...formData,
                startDate: dates[0],
                endDate: dates[1],
            })
        }
    }

    const applyFiltersHandler = () => {
        pageRef.current = 0
        searchFn()
    }
    const onSyncBlazeProducts = () => {
        dispatch(syncOrderAdmin({ startDate: new Date(formData.startDate).getTime(), endDate: new Date(formData.endDate).getTime() })).then(() => {
            searchFn()
        });
    };

    const waitingTime = '';

    return (
        <>
            <Grid container alignItems="center" rowSpacing={2} columnSpacing={2} mb={3}>
                <Grid item xs={12} >

                    <div className="last-sync">
                        <div>
                        </div>
                        <LightTooltip title={`${syncOrder ? 'Syncing is in Progress' : 'Sync receipts'}`}>
                            <div className={`product-sync-div ${waitingTime ? 'disabled-text' : ''}`} onClick={onSyncBlazeProducts}>
                                {syncOrder ? <CircularProgress size={18} /> : waitingTime ? waitingTime : `Sync Receipts`}
                            </div>
                        </LightTooltip>
                    </div>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Typography variant='h4'>Orders</Typography>
                </Grid>

                <Grid item xs={12} sm={8}>
                    <Stack direction="row" justifyContent={{ xs: 'start', sm: 'end' }} flexWrap="wrap" alignItems="stretch" gap={2}>


                        <Grid item xs={12} md={9}>
                            <Stack direction="row" justifyContent={{ xs: "start", md: "end" }} flexWrap="wrap" alignItems="stretch" gap={2}>
                                <TextField className="custom-search-field" placeholder="Search" label="" value={searchTerm} onChange={(e) => searchHandler(e.target.value)}
                                    sx={{ minWidth: "300px" }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">
                                            <IconButton size='small'>
                                                <SearchIcon width='18px' height='18px' />
                                            </IconButton>
                                        </InputAdornment>,
                                        endAdornment: searchTerm?.length > 0 ? <InputAdornment>
                                            <IconButton onClick={() => searchHandler('')} size='small'>
                                                <CloseRounded />
                                            </IconButton>
                                        </InputAdornment> : null
                                    }}
                                />
                                {/* <Search placeholder="Search" label="" value={searchTerm} onChange={searchHandler} /> */}
                                <Box className="filter-date-range" style={{ minWidth: "280px" }}>
                                    <FormControl fullWidth>
                                        <DateRange setDateRange={setDateRange} name='range' dateRange={[formData.startDate, formData.endDate]}
                                        />
                                    </FormControl>
                                </Box>
                                <MDButton className="filter-apply" size="small" variant="contained" color="primary"
                                    onClick={applyFiltersHandler} style={{ minWidth: "120px" }}
                                    disabled={!formData.startDate || !formData.endDate
                                        // || (new Date(formData.endDate) - new Date(formData.startDate)) > 30 * 24 * 60 * 60 * 1000        // // Disable if the date range is more than 30 days
                                    }
                                >
                                    Apply
                                </MDButton>
                            </Stack>
                        </Grid>
                        {/* <MDButton
                            startIcon={<AddRoundedIcon fontSize='large' />}
                            variant="contained" color="primary"
                            onClick={() => {
                                dispatch(openCustomModalDialog({
                                    modal: 'ADD_METRC_DATA',
                                    modalData: {
                                        type: "Member",
                                        title: 'Add Member',
                                        data: {},
                                        edit: false,
                                        metrcHandler: metrcHandler
                                    }
                                }))
                            }} >
                            Add Member
                        </MDButton> */}
                    </Stack>
                </Grid>
            </Grid>

            <AgGridTable col={columnDefs} getRows={getRows} mainGridApiRef={mainGridApiRef} pageRef={pageRef}
                isLoading={loading} onRowClicked={onRowClicked} style={{ height: '71vh' }} />
        </>
    );
}